import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getLymphInfo, resolveLymphName } from './lymphData';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';
const HIGHLIGHT_COLOR = new THREE.Color(0x7BD6E0);

function LymphaticSystemFull() {
  const navigate = useNavigate();
  const modelRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);

  const originalMaterialsRef = useRef(new Map());
  const highlightedMeshRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;
    if (mv.requestUpdate) mv.requestUpdate();
    try {
      const expAttr = mv.getAttribute('exposure') || '1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
      });
    } catch (e) { }
  }, []);

  const getInternalScene = useCallback(() => {
    if (sceneRef.current && cameraRef.current) return { scene: sceneRef.current, camera: cameraRef.current };
    const mv = modelRef.current;
    if (!mv) return null;

    let scene = null, camera = null;
    const symbols = Object.getOwnPropertySymbols(mv);
    for (const sym of symbols) {
      const val = mv[sym];
      if (val && val.isScene) scene = val;
      if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
      if (val && val.scene && val.scene.isScene) scene = val.scene;
      if (val && val.camera && (val.camera.isCamera || val.camera.isPerspectiveCamera)) camera = val.camera;
    }
    if (!scene) {
      const keys = Object.getOwnPropertyNames(mv);
      for (const key of keys) {
        try {
          const val = mv[key];
          if (val && val.isScene) scene = val;
          if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
        } catch (e) {}
      }
    }
    if (scene) {
      sceneRef.current = scene;
      cameraRef.current = camera || mv.camera;
      return { scene: sceneRef.current, camera: cameraRef.current };
    }
    return null;
  }, []);

  const ensureMaterialSaved = useCallback((mesh) => {
    if (originalMaterialsRef.current.has(mesh.uuid)) return;
    const orig = mesh.material;
    let cloned;
    if (Array.isArray(orig)) {
      cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
    } else if (orig && typeof orig.clone === 'function') {
      cloned = orig.clone();
    }
    if (!cloned) return;
    mesh.material = cloned;

    const mats = Array.isArray(cloned) ? cloned : [cloned];
    const props = mats.map(m => ({
      color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
      opacity: m.opacity ?? 1,
      transparent: !!m.transparent,
      emissive: (m.emissive && typeof m.emissive.clone === 'function') ? m.emissive.clone() : new THREE.Color(0x000000),
      emissiveIntensity: m.emissiveIntensity ?? 1
    }));
    originalMaterialsRef.current.set(mesh.uuid, props);
  }, []);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;
    ensureMaterialSaved(mesh);

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
      if (!m) return;
      if (m.color) m.color.set(HIGHLIGHT_COLOR);
      m.opacity = 0.75;
      m.transparent = true;
    });

    highlightedMeshRef.current = mesh;
    forceRender();
  }, [forceRender, ensureMaterialSaved]);

  const restoreMesh = useCallback((mesh) => {
    const props = originalMaterialsRef.current.get(mesh.uuid);
    if (!props) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m, i) => {
      const p = props[i];
      if (m && p) {
        if (m.color && p.color) m.color.set(p.color);
        m.opacity = p.opacity;
        m.transparent = p.transparent;
        if (m.emissive && p.emissive) m.emissive.set(p.emissive);
        if (p.emissiveIntensity !== undefined) m.emissiveIntensity = p.emissiveIntensity;
      }
    });
  }, []);

  const resetHighlight = useCallback(() => {
    const high = highlightedMeshRef.current;
    if (!high || !high.isMesh) return;
    restoreMesh(high);
    highlightedMeshRef.current = null;
    forceRender();
  }, [restoreMesh, forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel } =
    useMeshSelection({ infoLookup: getLymphInfo, highlightMesh, resetHighlight });

  const handleModelClick = useCallback((event) => {
    const mv = modelRef.current;
    if (!mv) return;

    const rect = mv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) {
      closeLabel();
      return;
    }

    const ctx = getInternalScene();
    let identifiedMesh = null;
    let identifiedName = null;

    if (ctx && ctx.camera) {
      const ndcX = (x / rect.width) * 2 - 1;
      const ndcY = -(y / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), ctx.camera);
      const intersects = raycasterRef.current.intersectObjects(ctx.scene.children, true);
      const hitResult = intersects.find(i => {
        const obj = i.object;
        if (!obj.isMesh) return false;
        const name = (obj.name || '').toLowerCase();
        const matName = (obj.material?.name || '').toLowerCase();
        // Filter out internal objects
        if (name.includes('shader') || name.includes('shadow') || name.includes('ground') || name.includes('helper')) return false;
        if (matName.includes('shader') || matName.includes('shadow') || matName.includes('ground')) return false;
        return true;
      });
      if (hitResult) {
        identifiedMesh = hitResult.object;
        identifiedName = resolveLymphName(identifiedMesh);
      }
    }

    if (!identifiedName) {
      const mvMat = mv.materialFromPoint(x, y);
      if (mvMat && mvMat.name) identifiedName = mvMat.name;
    }

    if (identifiedName && identifiedName !== 'Unknown') {
      applySelection(identifiedMesh, hit.position, hit.normal, identifiedName);
    } else {
      closeLabel();
    }
  }, [getInternalScene, applySelection, closeLabel]);

  const onFound = useCallback((mesh, center, normal) => {
    const resolved = resolveLymphName(mesh);
    applySelection(mesh, center, normal, resolved);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound });

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;
    const handleLoad = () => {
      setIsLoading(false);
      focusMeshIfPending();
    };
    mv.addEventListener('load', handleLoad);
    if (mv.loaded) handleLoad();
    return () => mv.removeEventListener('load', handleLoad);
  }, [getInternalScene, focusMeshIfPending]);

  return (
    <div className="model-viewer-page skeleton-page skeleton-surface-click">
      <button onClick={() => navigate('/menu')} className="back-btn">← Back to Menu</button>

      <ModelSearchBar currentSystemId="lymphaticsystem" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading 3D Lymphatic System...</p>
        </div>
      )}

      <model-viewer
        ref={modelRef}
        src="/models/LymphaticSystem.glb"
        ios-src="/models/LymphaticSystem.usdz"
        alt="3D Lymphatic System Model"
        ar
        ar-modes="webxr scene-viewer quick-look"
        camera-controls
        touch-action="none"
        shadow-intensity="1"
        loading="eager"
        reveal="auto"
        interaction-prompt="none"
        quick-look-browsers="safari chrome"
        camera-orbit="0deg 75deg 2m"
        field-of-view="45deg"
        min-camera-orbit="auto auto 0.05m"
        max-camera-orbit="auto auto 4m"
        onClick={handleModelClick}
        style={{ width: '100%', height: '100%', backgroundColor: '#ffffff', cursor: 'var(--cursor-pointer)', '--progress-bar-color': '#00E5FF', '--progress-bar-height': '4px' }}
      >
        {selectedPart && hotspotPosition && (
          <div
            key={hotspotKey}
            className={`skeleton-leader-hotspot ${screenSide}`}
            slot="hotspot-part"
            data-position={hotspotPosition.position}
            data-normal={hotspotPosition.normal}
          >
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-content">
                <span className="skeleton-leader-english">{selectedPart.english}</span>
                <span className="skeleton-leader-latin">{selectedPart.latin}</span>
              </div>
              <button className="skeleton-leader-close" onClick={closeLabel}>✕</button>
            </div>
          </div>
        )}
      </model-viewer>
    </div>
  );
}

function LymphaticSystem() {
  return <LymphaticSystemFull />;
}

export default LymphaticSystem;
